import {
  JPEG_QUALITY,
  MAX_OUTPUT_PIXELS,
  normalizeRotation,
  rotateSize
} from './imageCropGeometry'

export function outputSizeForCrop(cropWidth, cropHeight, maxPixels = MAX_OUTPUT_PIXELS) {
  const width = Math.max(1, Math.round(cropWidth))
  const height = Math.max(1, Math.round(cropHeight))
  const pixels = width * height
  if (pixels <= maxPixels) {
    return { width, height, scale: 1 }
  }
  const scale = Math.sqrt(maxPixels / pixels)
  return {
    width: Math.max(1, Math.floor(width * scale)),
    height: Math.max(1, Math.floor(height * scale)),
    scale
  }
}

export function sourceToBlob(src) {
  if (!src) {
    return Promise.reject(new Error('No image to export'))
  }
  return fetch(src).then((response) => {
    if (!response.ok) {
      throw new Error('Could not read the original image')
    }
    return response.blob()
  })
}

/**
 * Export the oriented crop as JPEG.
 *
 * `orientedPixelCrop` is in the rotated image's bounding-box pixel space
 * (the same space the on-screen crop window maps to). Canvas size is the
 * crop, not the full photo, so 48MP sources do not allocate a 48MP canvas.
 */
export function exportOrientedCropToJpeg(image, orientedPixelCrop, rotation = 0, options = {}) {
  return new Promise((resolve, reject) => {
    if (!image || !image.naturalWidth || !image.naturalHeight) {
      reject(new Error('Image is not ready to crop'))
      return
    }
    if (!orientedPixelCrop || orientedPixelCrop.width < 1 || orientedPixelCrop.height < 1) {
      reject(new Error('Crop area is too small'))
      return
    }

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      reject(new Error('This browser cannot crop images'))
      return
    }

    const quality = options.quality == null ? JPEG_QUALITY : options.quality
    const maxPixels = options.maxPixels == null ? MAX_OUTPUT_PIXELS : options.maxPixels
    const cropX = orientedPixelCrop.x
    const cropY = orientedPixelCrop.y
    const cropW = orientedPixelCrop.width
    const cropH = orientedPixelCrop.height
    const dest = outputSizeForCrop(cropW, cropH, maxPixels)
    const rot = normalizeRotation(rotation)
    const rotRad = (rot * Math.PI) / 180
    const imgW = image.naturalWidth
    const imgH = image.naturalHeight
    const bbox = rotateSize(imgW, imgH, rot)

    canvas.width = dest.width
    canvas.height = dest.height
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, dest.width, dest.height)
    ctx.imageSmoothingEnabled = dest.width !== cropW || dest.height !== cropH
    ctx.imageSmoothingQuality = 'high'

    ctx.save()
    if (dest.width !== cropW || dest.height !== cropH) {
      ctx.scale(dest.width / cropW, dest.height / cropH)
    }
    ctx.translate(-cropX, -cropY)
    ctx.translate(bbox.width / 2, bbox.height / 2)
    ctx.rotate(rotRad)
    ctx.translate(-imgW / 2, -imgH / 2)
    try {
      ctx.drawImage(image, 0, 0)
    } catch (error) {
      reject(error)
      return
    }
    ctx.restore()

    canvas.toBlob(
      (blob) => {
        canvas.width = 0
        canvas.height = 0
        if (!blob) {
          reject(new Error('Could not encode the cropped image'))
          return
        }
        resolve(blob)
      },
      'image/jpeg',
      quality
    )
  })
}
